import { ReactNode, useCallback } from 'react';

import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import {
  ImagePickerContext,
  ImagePickerFn,
  ImagePickerOptions,
  useTranslation,
} from '@scorenco/core';

import { T_KEYS } from '../translations';

const isUserCancellation = (error: unknown): boolean => {
  const e = error as { message?: string };
  return (
    e?.message === 'User cancelled photos app' ||
    e?.message === 'User rejected permissions'
  );
};

export const CapacitorImagePickerProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { t } = useTranslation();

  const pickImage: ImagePickerFn = useCallback(
    async (options?: ImagePickerOptions) => {
      try {
        const image = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          source:
            options?.source === 'prompt'
              ? CameraSource.Prompt
              : CameraSource.Photos,
          resultType: CameraResultType.Base64,
          promptLabelHeader: t(T_KEYS.PICTURE_TITLE) || undefined,
          promptLabelCancel: t(T_KEYS.PICTURE_CANCEL) || undefined,
          promptLabelPhoto: t(T_KEYS.PICTURE_FROM_PHOTOS) || undefined,
          promptLabelPicture: t(T_KEYS.PICTURE_TAKE_PICTURE) || undefined,
        });
        return image.base64String ?? null;
      } catch (error) {
        if (isUserCancellation(error)) {
          return null;
        }
        throw error;
      }
    },
    [t]
  );

  return (
    <ImagePickerContext.Provider value={pickImage}>
      {children}
    </ImagePickerContext.Provider>
  );
};
